import { Button, Stack, TextField, Toolbar, Typography } from '@mui/material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { DateTimePicker } from '@mui/x-date-pickers'
import { useDateFormatter } from '../generic/hooks/useDateFormatter'
import { AddCircleRounded, Save, SaveAsRounded, SearchRounded, TextSnippetRounded, WindowRounded } from '@mui/icons-material'
import { apiGet, apiPost } from '../generic/Api_Functions'
import { useEffect, useState } from 'react'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import DeliveryNoteSelector from './DeliveryNoteSelector'
import AlertUI from '../generic/AlertUI'
import Document from '../configuration/document/Document'
import { useNavigate } from 'react-router-dom'
import WarningIcon from '@mui/icons-material/Warning'
import { useAuthorization } from '../generic/hooks/useAuthorization'

/**
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNoteDataAction} DeliveryNoteDataAction DataAction object
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNote} DeliveryNote Data object
 */

/**
 * @param {object} props
 * @param {DeliveryNote} props.deliveryNote
 * @param {React.Dispatch<DeliveryNoteDataAction>} props.dispatchDeliveryNote
 * @param {function} props.showAlert
 * @param {function(string Date)} props.onSave
 * @returns

 */
const MaterialReceptionHeader = ({ deliveryNote, dispatchDeliveryNote, showAlert, onSave, window }) => {
  const { t_ } = useSmartTranslation()
  const { format } = useDateFormatter()
  const [currentDeliveryNoteNumber, setCurrentDeliveryNoteNumber] = useState(deliveryNote?.number)
  const [currentDeliveryNoteDate, setCurrentDeliveryNoteDate] = useState(deliveryNote?.date ? new Date(deliveryNote.date) : new Date())
  const [openDeliveryNotesDialog, setOpenDeliveryNotesDialog] = useState(false)
  const [openConfirmSaveDialog, setOpenConfirmSaveDialog] = useState(false)
  const [openEmptyDialog, setOpenEmptyDialog] = useState(false)
  const [openNewDeliveryNoteDialog, setOpenNewDeliveryNoteDialog] = useState(false)
  const [openDocumentDialog, setOpenDocumentDialog] = useState(false)
  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null)
  const [AlertElementDeliveryNoteSelector, showAlertDeliveryNoteSelector] = AlertUI()
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [deliveryNoteNumberMaxLength, setDeliveryNoteNumberMaxLength] = useState(null)
  const [isDeliveryNoteMaxLengthLoaded, setIsDeliveryNoteMaxLengthLoaded] = useState(false)
  const navigate = useNavigate()
  const isUserAuthorized = useAuthorization()
  useEffect(() => {
    const getDeliveryNoteNumberMaxLength = async () => {
      try {
        const result = await apiGet('deliveryNote/getDeliveryNoteMaxLength')
        const num = Number(result)
        const sanitizedNum = Number.isFinite(num) ? Math.trunc(num) : null
        setDeliveryNoteNumberMaxLength(sanitizedNum > 0 ? sanitizedNum : null)
      } catch (error) {
        console.error('Error getting delivery note max length', error)
        setDeliveryNoteNumberMaxLength(null)
      } finally {
        setIsDeliveryNoteMaxLengthLoaded(true)
      }
    }
    getDeliveryNoteNumberMaxLength()
  }, [])
  const checkData = () => {
    if (!currentDeliveryNoteDate) {
      showAlert({ severity: 'warning', title: t_('No date selected') })
      return false
    }
    if (!currentDeliveryNoteNumber || currentDeliveryNoteNumber === '') {
      showAlert({ severity: 'warning', title: t_('No delivery note number') })
      return false
    }
    return true
  }

  const onCreateNew = async () => {
    if (!checkData()) {
      return
    }

    const data = {
      date: currentDeliveryNoteDate,
      number: currentDeliveryNoteNumber
    }
    const result = await apiPost('deliveryNote/create', data)
    dispatchDeliveryNote({ type: 'SET_DELIVERY_NOTE', deliveryNote: result })
  }

  const onpreSave = async () => {
    setOpenConfirmSaveDialog(false)
    if (!checkData()) {
      return
    }
    try {
      const result = await onSave(currentDeliveryNoteNumber, currentDeliveryNoteDate)

      if (!result) {
        return
      }

      showAlert({ severity: 'success', title: t_('Delivery note saved') })
    } catch (error) {
      showAlert({ severity: 'error', title: t_('Error saving delivery note'), message: error.message })
    }
  }

  const empty = () => {
    dispatchDeliveryNote({ type: 'RESET_VALUES' })
    setCurrentDeliveryNoteDate(new Date())
    setCurrentDeliveryNoteNumber('')
    setOpenEmptyDialog(false)
  }

  const selectNewDeliveryNote = () => {
    if (!selectedDeliveryNote) {
      showAlertDeliveryNoteSelector({ severity: 'warning', title: t_('No delivery note selected') })
      return
    }

    dispatchDeliveryNote({ type: 'SET_DELIVERY_NOTE', deliveryNote: selectedDeliveryNote })
    setCurrentDeliveryNoteDate(new Date(selectedDeliveryNote.date))
    setCurrentDeliveryNoteNumber(selectedDeliveryNote.number)
    setSelectedDeliveryNote(null)
    setOpenNewDeliveryNoteDialog(false)
    setOpenDeliveryNotesDialog(false)
  }

  return (
    <>
      <SmartDialog
        closeCallback={() => setOpenDocumentDialog(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<Document reference={{ id: deliveryNote?.id, code: deliveryNote?.number, name: deliveryNote?.number }} resourceType='DELIVERY_NOTE' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={openDocumentDialog}
      />
      <SmartDialog
        title={{
          visible: true,
          icon: <Save sx={{ fontSize: '30px' }} color='primary' />,
          render: <Typography color='primary' variant='h6'>{t_('Save data?')}</Typography>
        }}
        renderComponent={t_('Are you sure you want to save data?')}
        setOpen={openConfirmSaveDialog}
        acceptCallback={() => onpreSave()}
        cancelCallback={() => setOpenConfirmSaveDialog(false)}
      />
      <SmartDialog
        title={{
          visible: true,
          icon: <Save sx={{ fontSize: '30px' }} color='primary' />,
          render: <Typography color='primary' variant='h6'>{t_('Empty selection?')}</Typography>
        }}
        renderComponent={t_('Are you sure you want to deselect current delivery note? Unsaved changes will be lost')}
        setOpen={openEmptyDialog}
        acceptCallback={() => empty()}
        cancelCallback={() => setOpenEmptyDialog(false)}
      />
      <SmartDialog
        title={{
          visible: true,
          icon: <Save sx={{ fontSize: '30px' }} color='primary' />,
          render: <Typography color='primary' variant='h6'>{t_('Empty selection?')}</Typography>
        }}
        renderComponent={t_('Are you sure you want to select another delivery note? Unsaved changes will be lost')}
        setOpen={openNewDeliveryNoteDialog}
        acceptCallback={() => selectNewDeliveryNote()}
        cancelCallback={() => setOpenNewDeliveryNoteDialog(false)}
      />
      <SmartDialog
        setOpen={openDeliveryNotesDialog}
        title={t_('Search delivery note')}
        acceptCallback={() => { deliveryNote?.recordCode ? setOpenNewDeliveryNoteDialog(true) : selectNewDeliveryNote() }}
        cancelCallback={() => { setSelectedDeliveryNote(null); setOpenDeliveryNotesDialog(false) }}
        renderComponent={
          <DeliveryNoteSelector
            alertElement={AlertElementDeliveryNoteSelector}
            setSelectedDeliveryNote={(row) => {
              const element = { ...row }
              delete element['smart-table-index']
              setSelectedDeliveryNote(element)
            }}
          />
        }
      />
      <Toolbar
        sx={{
          position: 'sticky',
          top: 'var(--smart-navigator-sticky-offset, 48px)',
          left: 0,
          zIndex: 40,
          mt: '1.25rem',
          mb: '0.7rem',
          pt: '0.7rem',
          pb: '0.4rem',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 1,
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          backgroundColor: '#424242'
        }}
        disableGutters
      >
        <Stack
          direction='row'
          spacing={1}
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: '#424242',
            flexWrap: 'wrap',
            rowGap: 1,
            flex: '1 1 auto',
            minWidth: 0,
            pr: 1
          }}
        >
          <TextField
            label={t_('Reg. code')}
            InputLabelProps={{
              shrink: true
            }}
            value={deliveryNote?.recordCode ? deliveryNote.recordCode : ''}
            disabled
            sx={{ flex: '1 1 11rem', minWidth: { xs: '100%', sm: '11rem' }, maxWidth: { xs: '100%', sm: '12rem' } }}
          />
          <DateTimePicker
            label={t_('Date')}
            slotProps={{ textField: { InputLabelProps: { shrink: true } } }}
            format={format.dateTime}
            ampm={false}
            value={currentDeliveryNoteDate}
            onChange={(newValue) => { setCurrentDeliveryNoteDate(newValue) }}
            sx={{ flex: '1 1 11rem', minWidth: { xs: '100%', sm: '11rem' }, maxWidth: { xs: '100%', sm: '12rem' } }}
          />
          <TextField
            label={t_('Delivery Note numb.')}
            InputLabelProps={{
              shrink: true
            }}
            value={deliveryNote?.number ? deliveryNote.number : currentDeliveryNoteNumber}
            onChange={(event) => { setCurrentDeliveryNoteNumber(event.target.value) }}
            inputProps={{
              readOnly: !isDeliveryNoteMaxLengthLoaded,
              maxLength: deliveryNoteNumberMaxLength || undefined
            }}
            sx={{ flex: '1 1 11rem', minWidth: { xs: '100%', sm: '11rem' }, maxWidth: { xs: '100%', sm: '12rem' } }}
          />
        </Stack>
        <Stack
          direction='row'
          spacing={1}
          sx={{
            position: 'sticky',
            right: 0,
            ml: 'auto',
            pl: 1,
            flexShrink: 0,
            flexWrap: 'wrap',
            rowGap: 1,
            justifyContent: 'flex-end',
            alignItems: 'center',
            backgroundColor: '#424242',
            zIndex: 2
          }}
        >
          <Button
            sx={{ borderColor: 'transparent', whiteSpace: 'nowrap' }}
            color='primary'
            variant='outlined'
            onClick={() => { setOpenDeliveryNotesDialog(true) }}
            disabled={!isUserAuthorized('DELIVERY_NOTE_SEARCH')}
          >
            <SearchRounded sx={{ fontSize: '1.2em' }} />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('Search')}</Typography>
          </Button>
          {!deliveryNote?.recordCode && (
            <Button
              sx={{ borderColor: 'transparent', whiteSpace: 'nowrap' }}
              color='primary'
              variant='outlined'
              onClick={() => { onCreateNew() }}
              disabled={!isUserAuthorized('DELIVERY_NOTE_ADD')}
            >
              <AddCircleRounded sx={{ fontSize: '1.2em' }} />
              <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
                {t_('New delivery note')}
              </Typography>
            </Button>
          )}
          {deliveryNote?.recordCode && (
            <Button
              sx={{ borderColor: 'transparent', whiteSpace: 'nowrap' }}
              color='primary'
              variant='outlined'
              onClick={() => { setOpenEmptyDialog(true) }}
              disabled={!deliveryNote?.recordCode}
            >
              <WindowRounded sx={{ fontSize: '1.2em' }} />
              <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
                {t_('Empty')}
              </Typography>
            </Button>
          )}
          <Button
            sx={{ borderColor: 'transparent', whiteSpace: 'nowrap' }}
            color='primary'
            variant='outlined'
            onClick={() => navigate('/mos/reception/incidence', { state: { deliveryNote, type: 'DELIVERY_NOTE', window } })}
            disabled={!isUserAuthorized('RECEPTION_INCIDENCE')}
          >
            <WarningIcon sx={{ fontSize: '1.2em' }} />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('Incidences')}</Typography>
          </Button>
          <Button
            sx={{ borderColor: 'transparent', whiteSpace: 'nowrap' }}
            color='primary'
            variant='outlined'
            onClick={() => setOpenConfirmSaveDialog(true)}
            disabled={!deliveryNote?.recordCode || !isUserAuthorized('DELIVERY_NOTE_SAVE')}
          >
            <SaveAsRounded sx={{ fontSize: '1.2em' }} />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('Save')}</Typography>
          </Button>
          <Button
            sx={{ borderColor: 'transparent', whiteSpace: 'nowrap' }}
            color='primary'
            variant='outlined'
            onClick={() => setOpenDocumentDialog(true)}
            disabled={!deliveryNote?.recordCode || !isUserAuthorized('DOCUMENTS_DELIVERY_NOTE')}
          >
            <TextSnippetRounded sx={{ fontSize: '1.2em' }} />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('Documents')}</Typography>
          </Button>
        </Stack>
      </Toolbar>
    </>
  )
}

export default MaterialReceptionHeader
